import React, { useMemo, useCallback, useEffect } from 'react';
import { track as peloTrack } from '@peloton/analytics/analytics';
import {
  toClientDetails,
  toEncodedClientDetails,
} from '@peloton/analytics/clientDetailsHeader';
import { isEmpty } from '@peloton/helpers/isEmpty';
import { isDefined } from '@peloton/types/isDefined';
import { track } from '@engage/analytics/actions';
import { useReduxAction } from '@engage/redux/hooks';
import { useMembersRouter as useRouter } from '@members/localization/useMembersRouter';
import { toBaseProperties } from '../webBaseProperties';

export enum AnalyticsEvent {
  ViewedLoginError = 'Viewed Login Error',
  UnlinkedSocialAccount = 'Unlinked Social Account',
  LinkedSocialAccount = 'Linked Social Account',
}

export type TrackingFn = (event: AnalyticsEvent, properties?: object) => void;

export const useTrackingCallback = () => {
  const segmentTrack: TrackingFn = useCallback((event, properties) => {
    peloTrack({
      event,
      ...toBaseProperties(),
      properties,
    });
  }, []);

  return { segmentTrack };
};

export const useTrackPageView: TrackingFn = (event, properties) => {
  const { segmentTrack } = useTrackingCallback();
  useEffect(() => {
    segmentTrack(event, properties);
  }, []);
};

export const useSendAnalyticOnMount = (
  analyticCallBack: (...args: any[]) => any,
  analyticOptionalParams: any[] = [],
) => {
  const trackAnalytics = useReduxAction(track);
  const [hasSentAnalytic, setHasSentAnalytic] = React.useState(false);

  React.useEffect(() => {
    if (
      analyticOptionalParams.every(e => isDefined(e) && !isEmpty(e) && e) &&
      !hasSentAnalytic
    ) {
      trackAnalytics(analyticCallBack(...analyticOptionalParams));
      setHasSentAnalytic(true);
    }
  }, [...analyticOptionalParams, hasSentAnalytic]);

  return null;
};

export const useClientDetailsHeader = (
  source: string,
  additionalDetails?: { [K: string]: string },
) => {
  return useMemo(() => {
    const baseProps = toBaseProperties();
    return toEncodedClientDetails({
      ...toClientDetails(baseProps, source),
      ...additionalDetails,
    });
  }, [source, additionalDetails]);
};

export const useUtmParams = () => {
  const { query } = useRouter();
  return {
    utm_source: (query?.utm_source as string | undefined) ?? '',
    utm_medium: (query?.utm_medium as string | undefined) ?? '',
    utm_content: (query?.utm_content as string | undefined) ?? '',
  };
};
